import React, { Component } from 'react'
import Translation from '../../Translation/cont/Translation'
import TranslationTime from '../../Translation/cont/TranslationTime'
import TranslationShowtime from '../../Translation/cont/TranslationShowtime'
import getImageUrl from '../../../functions/getImageUrl'
import getFSKColors from '../../PageNewsletterTemplate/functions/getFSKColors'
import ImageWithFallback from '../../general/ImageWithFallback'
import THEME_FONT_FAMILIES from '../../../constants/themeFontFamilies'

const CANVAS_MAX_WIDTH = 540

const POSITION_STYLE = {
    'top-left':      { top: '5%',  left: '5%' },
    'top-center':    { top: '5%',  left: '50%', transform: 'translateX(-50%)' },
    'top-right':     { top: '5%',  right: '5%' },
    'center-left':   { top: '50%', left: '5%',  transform: 'translateY(-50%)' },
    'center':        { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' },
    'center-right':  { top: '50%', right: '5%', transform: 'translateY(-50%)' },
    'bottom-left':   { bottom: '5%', left: '5%' },
    'bottom-center': { bottom: '5%', left: '50%', transform: 'translateX(-50%)' },
    'bottom-right':  { bottom: '5%', right: '5%' },
}

function posStyle(pos) {
    return Object.assign({ position: 'absolute' }, POSITION_STYLE[pos] || POSITION_STYLE['bottom-center'])
}

function fontStack(font, systemFont) {
    if (font && font !== 'inherit') return `${font}, sans-serif`
    return systemFont ? `${systemFont}, sans-serif` : 'inherit'
}

class PostPreview extends Component {
    getMovie() {
        const { movies, image } = this.props
        if (!movies || !image.movieId) return null
        if (Array.isArray(movies)) {
            return movies.find(m => String(m.id || m.pk) === String(image.movieId)) || null
        }
        return movies[image.movieId] || null
    }

    getEffectivePreset() {
        const { preset, presets, image } = this.props
        const overridePresetId = image ? image.presetId : null
        if (!overridePresetId || !presets) return preset

        const overridePreset = Array.isArray(presets)
            ? presets.find(p => String(p.id) === String(overridePresetId))
            : presets[overridePresetId]
        return overridePreset || preset
    }

    getPerformanceText() {
        const { performances, image } = this.props
        if (!performances || !image.performanceId) return null
        const list = Array.isArray(performances) ? performances : Object.values(performances)
        const perf = list.find(p => String(p.id || p.pk) === String(image.performanceId))
        if (!perf) return null
        return (
            <>
                <TranslationTime id="date" timeString={perf.timeUtc} /> <TranslationShowtime string={perf.timeUtc} /> <Translation id="movie_sort_clock"/>
            </>
        )
    }

    render() {
        const { image, format, formatId, customWidth, customHeight, themeData, front } = this.props
        if (!image) return <div className="post-preview post-preview--empty" />

        const fw = formatId === 'custom' ? customWidth  : (format ? format.width  : 1080)
        const fh = formatId === 'custom' ? customHeight : (format ? format.height : 1350)
        const canvasAspect = fw / fh
        const PREVIEW_H    = Math.round(CANVAS_MAX_WIDTH * fh / fw)

        const sc = (px) => Math.max(7, Math.round((px || 0) * CANVAS_MAX_WIDTH / fw))

        const preset       = this.getEffectivePreset()
        const settings     = (preset && preset.settings) ? preset.settings : {}
        const gen          = settings.general   || {}
        const bg           = settings.background || {}
        const brd          = settings.border     || {}
        const grad         = settings.gradient   || {}
        const ttl          = settings.title      || {}
        const ft           = settings.freetext   || {}
        const dt           = settings.datetime   || {}
        const badge        = settings.badge      || {}
        const fsk          = settings.fsk        || {}
        const logo         = settings.logo       || {}
        const cta          = settings.cta        || {}

        const chosenFont       = (themeData && themeData.chosen_font) || 'affogato'
        const systemFontFamily = THEME_FONT_FAMILIES[chosenFont] || THEME_FONT_FAMILIES.affogato

        const posterMaxWidth      = gen.posterMaxWidth || 50
        const effectivePosterWidth = Math.min(posterMaxWidth, Math.floor(50 / canvasAspect))
        const posterX = gen.posterX != null ? gen.posterX : 50
        const posterY = gen.posterY != null ? gen.posterY : 50

        // ── Border ──────────────────────────────────────────────────────────
        const borderStyle = image.borderEnabled && brd.width > 0
            ? { border: `${brd.width}px ${brd.style || 'solid'} ${brd.color || '#fff'}`, boxSizing: 'border-box' }
            : {}

        let cornerStyle = {}
        if (image.borderEnabled) {
            if (brd.corners === 'rund') {
                cornerStyle = { borderRadius: `${brd.borderRadius || 0}px` }
            } else if (brd.corners === 'abgeschrägt') {
                const sz  = sc(brd.bevelSize || 20)
                const bc  = brd.bevelCorners || {}
                const tl  = bc.ol ? sz : 0
                const tr  = bc.or ? sz : 0
                const bl  = bc.ul ? sz : 0
                const br_ = bc.ur ? sz : 0
                cornerStyle = {
                    clipPath: `polygon(
                        ${tl}px 0, calc(100% - ${tr}px) 0,
                        100% ${tr}px, 100% calc(100% - ${br_}px),
                        calc(100% - ${br_}px) 100%, ${bl}px 100%,
                        0 calc(100% - ${bl}px), 0 ${tl}px
                    )`,
                }
            }
        }

        // ── Gradient overlay ────────────────────────────────────────────────
        let gradientOverlay = null
        if (image.gradientEnabled && grad.enabled && grad.stops && grad.stops.length >= 2) {
            const sortedStops = grad.stops
                .slice()
                .sort((a, b) => a.position - b.position)
                .map(stop => `${stop.color} ${stop.position}%`)
                .join(', ')
            let gradCss
            if (grad.type === 'radial') {
                gradCss = `radial-gradient(circle, ${sortedStops})`
            } else if (grad.type === 'conic') {
                gradCss = `conic-gradient(${sortedStops})`
            } else {
                gradCss = `linear-gradient(${grad.angle || 180}deg, ${sortedStops})`
            }
            gradientOverlay = (
                <div style={{ position: 'absolute', inset: 0, background: gradCss, pointerEvents: 'none' }} />
            )
        }

        // ── Movie & text ────────────────────────────────────────────────────
        const movie     = this.getMovie()
        const posterUrl = movie && movie.images && movie.images.poster ? movie.images.poster.url : null
        const posterUrlData = getImageUrl(posterUrl, false, true, false, true)
        const finalPosterUrl = posterUrlData.finalImageURl ? posterUrlData.finalImageURl : posterUrlData.imageUrl

        const bgUrlData = bg.imageUrl ? getImageUrl(bg.imageUrl, false, false, false, true) : null
        const finalBgUrl = bgUrlData ? bgUrlData.finalImageURl ? bgUrlData.finalImageURl : bgUrlData.imageUrl : null

        const movieBgUrlData = (image.backdropEnabled && image.backdropUrl) ? getImageUrl(image.backdropUrl, false, false, false, true) : null
        const finalMovieBgUrl = movieBgUrlData ? movieBgUrlData.finalImageURl ? movieBgUrlData.finalImageURl : movieBgUrlData.imageUrl : null

        const titleText = image.titleEnabled
            ? (image.titleText || (movie ? (movie.titleDisplay || movie.title) : ''))
            : null
        const freetextText = image.freetextEnabled ? image.freetextText : null
        const dateText  = image.dateEnabled
            ? (image.dateMode === 'manual' ? image.dateManual : this.getPerformanceText())
            : null

        // ── Text group ───────────────────────────────────────────────────────
        const groupAnchor  = ttl.position || 'bottom-center'
        const textRowStyle = (data, defaults) => {
            const style = {
                color:      data.color || defaults.color,
                fontSize:   `${sc(data.size || defaults.size)}px`,
                fontFamily: fontStack(data.font, systemFontFamily),
                textAlign:  data.align || defaults.align || 'center',
                lineHeight: 1.3,
            }
            if (data.backgroundColor && data.backgroundColor !== 'transparent') {
                style.background = data.backgroundColor
                style.padding = '1px 4px'
            }
            return style
        }

        // ── Badge shape ──────────────────────────────────────────────────────
        const badgeShapeStyle = badge.shape === 'circle'  ? { borderRadius: '50%' }
            : badge.shape === 'rounded' ? { borderRadius: `${badge.borderRadius || 0}px` }
            : {}

        // ── FSK badge colors (mirrors front-end FSK rating colors + neutral-colors toggle) ──
        const themeVariant = (themeData && themeData.theme_colors) || 'Dark'
        const neutralFsk    = !!(front && front.set_netural_colors_for_fsk)
        const fskColorMap   = getFSKColors(themeVariant)() || {}
        const fskKey        = movie && movie.fsk != null ? `fsk-${movie.fsk}` : 'fsk-unknown'
        const fskColor      = fskColorMap[fskKey] || fskColorMap['fsk-unknown'] || '#cc0000'
        const fskTextColor  = movie && movie.fsk === 18 ? '#fff' : '#181818'
        const fskBadgeColorStyle = neutralFsk
            ? { background: 'rgba(24, 24, 24, .75)', color: '#fff', border: 'none' }
            : { background: `${fskColor}bf`, color: fskTextColor, border: `0px solid ${fskColor}` }

        return (
            <div className="post-preview">
                <div style={{
                    position:   'relative',
                    overflow:   'hidden',
                    width:      CANVAS_MAX_WIDTH,
                    height:     PREVIEW_H,
                    flexShrink: 0,
                    background: bg.color || '#1a1a2e',
                    ...borderStyle,
                    ...cornerStyle,
                }}>

                    {/* Background image */}
                    {finalBgUrl && (
                        <ImageWithFallback src={finalBgUrl} srcFallback={bgUrlData.imageUrl} alt="" style={{
                            position: 'absolute', inset: 0,
                            width: '100%', height: '100%', objectFit: 'cover',
                            pointerEvents: 'none',
                        }} />
                    )}

                    {/* Backdrop */}
                    {image.backdropEnabled && finalMovieBgUrl && (
                        <ImageWithFallback src={finalMovieBgUrl} srcFallback={movieBgUrlData.imageUrl} alt="" style={{
                            position: 'absolute', inset: 0,
                            width: '100%', height: '100%', objectFit: 'cover',
                        }} />
                    )}

                    {/* Gradient overlay */}
                    {gradientOverlay}

                    {/* Poster */}
                    {image.posterEnabled && (
                        <div style={{
                            position:  'absolute',
                            left:      `${posterX}%`,
                            top:       `${posterY}%`,
                            transform: 'translate(-50%, -50%)',
                            width:     `${effectivePosterWidth}%`,
                        }}>
                            {finalPosterUrl ? (
                                <ImageWithFallback src={finalPosterUrl} srcFallback={posterUrlData.imageUrl} alt="" style={{
                                    width: '100%', display: 'block', aspectRatio: '2/3', objectFit: 'cover',
                                }} />
                            ) : (
                                <div style={{
                                    background:  'linear-gradient(180deg, #28183A 0%, #0E121C 100%)',
                                    aspectRatio: '2/3',
                                    display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
                                    padding: `${sc(8)}px`,
                                }}>
                                    <span style={{
                                        color: '#EDE0C8', fontSize: `${sc(11)}px`,
                                        fontWeight: 700, textTransform: 'uppercase', textAlign: 'center',
                                    }}>
                                        {movie ? (movie.titleDisplay || movie.title) : <Translation id="movie_edit_movie_title" />}
                                    </span>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Text group */}
                    {(titleText || freetextText || dateText) && (
                        <div style={Object.assign({ width: 'fit-content', maxWidth: '90%', pointerEvents: 'none' }, posStyle(groupAnchor))}>
                            {titleText && (
                                <div style={textRowStyle(ttl, { align: 'center', size: 72, color: '#ffffff' })}>
                                    {titleText}
                                </div>
                            )}
                            {freetextText && (
                                <div style={textRowStyle(ft, { align: 'center', size: 48, color: '#ffffff' })}>
                                    {freetextText}
                                </div>
                            )}
                            {dateText && (
                                <div style={textRowStyle(dt, { align: 'center', size: 36, color: '#C8C0B0' })}>
                                    {dateText}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Badge */}
                    {image.badgeEnabled && badge.enabled !== false && (
                        <div style={{
                            position:       'absolute',
                            left:           `${badge.x != null ? badge.x : 80}%`,
                            top:            `${badge.y != null ? badge.y : 60}%`,
                            transform:      'translate(-50%, -50%)',
                            width:          `${sc(badge.width || 200)}px`,
                            height:         `${sc(badge.height || 200)}px`,
                            background:     badge.backgroundColor || '#ef7d00',
                            color:          badge.color || '#fff',
                            display:        'flex',
                            alignItems:     'center',
                            justifyContent: 'center',
                            fontSize:       `${Math.max(6, sc((badge.width || 200) / 4))}px`,
                            fontWeight:     700,
                            textAlign:      'center',
                            overflow:       'hidden',
                            pointerEvents:  'none',
                            ...badgeShapeStyle,
                        }}>
                            {image.badgeText || badge.defaultText || 'NEU'}
                        </div>
                    )}

                    {/* FSK badge */}
                    {image.fskEnabled && fsk.enabled && movie && movie.fsk != null && (
                        <div style={{
                            ...posStyle(fsk.position || 'bottom-left'),
                            height:         `${sc(fsk.size || 100)}px`,
                            padding:        `0 ${sc(20)}px`,
                            borderRadius:   `${sc(10)}px`,
                            boxSizing:      'border-box',
                            display:        'inline-flex',
                            alignItems:     'center',
                            justifyContent: 'center',
                            fontSize:       `${Math.max(6, sc((fsk.size || 100) / 2.8))}px`,
                            fontWeight:     700,
                            whiteSpace:     'nowrap',
                            pointerEvents:  'none',
                            ...fskBadgeColorStyle,
                        }}>
                            FSK {movie.fsk}
                        </div>
                    )}

                    {/* Logo */}
                    {image.logoEnabled && (image.logoOverrideUrl || logo.imageUrl) && (
                        <img src={image.logoOverrideUrl || logo.imageUrl} alt="" style={{
                            ...posStyle(logo.position || 'bottom-right'),
                            width:        `${sc(logo.size || 150)}px`,
                            height:       'auto',
                            maxWidth:     '30%',
                            pointerEvents: 'none',
                        }} />
                    )}

                    {/* CTA */}
                    {image.ctaEnabled && image.ctaText && (
                        <div style={Object.assign({
                            color:      cta.color || '#ffffff',
                            background: cta.backgroundColor && cta.backgroundColor !== 'transparent' ? cta.backgroundColor : '#ef7d00',
                            fontFamily: fontStack(cta.font, systemFontFamily),
                            fontSize:   `${sc(cta.size || 32)}px`,
                            fontWeight: 700,
                            textAlign:  cta.align || 'center',
                            lineHeight: 1.3,
                            padding:    `${sc(8)}px ${sc(20)}px`,
                            whiteSpace: 'nowrap',
                            pointerEvents: 'none',
                        }, posStyle(cta.position || 'bottom-center'))}>
                            {image.ctaText}
                        </div>
                    )}
                </div>
            </div>
        )
    }
}

export default PostPreview
